import type { Getter, Setter, Atom, WritableAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useEffect, useState } from 'react';

let _get!: Getter;
let _set!: Setter;

// This hook allows to use atom outside of React, but in a React project
// for example we can use it in helpers functions that aren't inside a hook or component
// but we need to initialize first in a hook or component
// to use it just add this hook in the root component of the app:
//   useJotaiOutsideReact();
// and then import where you want to use it:
//  import { readAtom, writeAtom } from '@openeducation/oe-rn-providers';

// "Inspired in" https://github.com/omerman/jotai-nexus

export const useJotaiOutsideReact = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initReader = useAtomCallback((get, set) => {
    _get = get;
    _set = set;
  });

  useEffect(() => {
    if (!isInitialized) {
      initReader();
      setIsInitialized(true);
    }
  }, [initReader, isInitialized]);
};

export const readAtom = <A extends Atom<any>>(
  a: A
): A extends Atom<infer Data> ? Data : never => _get(a);

export const writeAtom = <A extends WritableAtom<any, any>>(
  a: A,
  update: A extends WritableAtom<any, infer Update> ? Update : never
) => a.write(_get, _set, update);
